import { useEffect, useState } from "react";
import { Sparkles, ShieldCheck, AlertTriangle, Info } from "lucide-react";
import { Navigate } from "react-router-dom";
import AppNav from "@/components/app/AppNav";
import AskInformedPanel from "@/components/academy/AskInformedPanel";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

/**
 * Informed OS Academy — Ask Informed (owner access page).
 *
 * Route: `/academy/ask`. Registered inside the authenticated section
 * of `App.js`. Visible ONLY to the account owner (`team_role === 'owner'`).
 * Non-owners are redirected to `/academy`; the server also enforces
 * the same `team_role == 'owner'` gate on every underlying Ask endpoint.
 *
 * Truthful status surface (per operator directive — no fake content,
 * no fake conversations, no hardcoded coach output):
 *   1. Owner access badge (small notice — "Ask Informed is available to
 *      the account owner"). Ask Informed is a real product; this notice
 *      only calls out the not-yet-public access distinction.
 *   2. Provider status notice — honestly reports that live AI responses
 *      are unavailable until the provider connection is verified.
 *   3. No-approved-material notice — honestly reports that no approved
 *      Academy course material has been published yet, so the coach
 *      cannot ground answers.
 *
 * On mount the page fetches `/academy/ask/owner-context` to resolve
 * the owner's working program (the first canonical Academy program).
 * The panel then creates a real conversation attached to that program.
 * Owner may type a question and submit it — the request goes through
 * the real backend pipeline; because `academy_grounding_units=0` the
 * server persists the question and returns the honest
 * `insufficient_grounding` response. Absolutely no client-side fake
 * assistant messages.
 */
export default function AcademyAskPage() {
  const { user, loading: authLoading } = useAuth();
  const [ctx, setCtx] = useState({ status: "loading", programId: null, title: null, ctxStatus: null, error: null });

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.team_role !== "owner") return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/academy/ask/owner-context");
        if (cancelled) return;
        setCtx({
          status: "ready",
          programId: r.data?.program_id || null,
          title: r.data?.title || null,
          ctxStatus: r.data?.status || null,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setCtx({ status: "error", programId: null, title: null, ctxStatus: null, error: formatApiError(err) });
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user]);

  if (authLoading) {
    return (
      <div
        data-testid="academy-ask-loading"
        className="min-h-screen bg-v2-background text-v2-text"
      >
        <AppNav />
      </div>
    );
  }
  if (!user || user.team_role !== "owner") {
    return <Navigate to="/academy" replace />;
  }
  return (
    <div
      data-testid="academy-ask-page"
      className="min-h-screen bg-v2-background text-v2-text"
    >
      <AppNav />
      <main
        data-testid="academy-ask-main"
        className="max-w-3xl mx-auto px-6 md:px-10 pt-14 pb-24"
      >
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg"
              style={{ background: "var(--v2-accent-soft)", color: "var(--v2-accent)" }}
            >
              <Sparkles className="w-5 h-5" />
            </span>
            <h1
              className="text-2xl font-semibold"
              style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
            >
              Ask Informed
            </h1>
          </div>
          <p
            data-testid="academy-ask-owner-notice"
            className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-v2-border bg-v2-surface text-v2-text-muted"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            Ask Informed is available to the account owner.
          </p>
        </header>

        {/* Truthful status notices — static, not derived from fake calls */}
        <section className="space-y-3 mb-8">
          <div
            data-testid="academy-ask-provider-status"
            className="flex items-start gap-3 p-4 rounded-lg border border-amber-500/40 bg-amber-500/5 text-amber-200"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p className="text-sm leading-relaxed">
              Live AI responses are temporarily unavailable because the
              provider connection requires verification.
            </p>
          </div>
          <div
            data-testid="academy-ask-content-status"
            className="flex items-start gap-3 p-4 rounded-lg border border-v2-border bg-v2-surface text-v2-text-muted"
          >
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p className="text-sm leading-relaxed">
              No approved Academy course material has been published
              yet, so the coach cannot ground answers in citable
              lessons at this time.
            </p>
          </div>
        </section>

        {/* Live Panel embed. `programId` comes from the owner-context
            fetch (first canonical Academy program) so the panel can
            create a REAL conversation and route a REAL question through
            the full server pipeline. Because grounding_units=0 the
            server returns the honest insufficient_grounding response —
            never a fake message. */}
        {ctx.status === "loading" && (
          <div
            data-testid="academy-ask-ctx-loading"
            className="text-sm text-v2-text-muted"
          >
            Preparing your workspace…
          </div>
        )}
        {ctx.status === "error" && (
          <div
            data-testid="academy-ask-ctx-error"
            className="rounded-lg border border-red-500/40 bg-red-500/5 p-4 text-sm text-red-300"
          >
            Couldn't load Ask Informed workspace: {ctx.error}
          </div>
        )}
        {ctx.status === "ready" && !ctx.programId && (
          <div
            data-testid="academy-ask-ctx-empty"
            className="rounded-lg border border-v2-border bg-v2-surface p-4 text-sm text-v2-text-muted"
          >
            No Academy program is available yet. Ask Informed will be
            ready to use once at least one canonical program exists.
          </div>
        )}
        {ctx.status === "ready" && ctx.programId && (
          <AskInformedPanel ownerAccess programId={ctx.programId} />
        )}
      </main>
    </div>
  );
}
